/* =========================================================================
 * WHO IS ASSIGNED TO A GOVERNING BODY — and the sort that makes the gap
 * reachable.
 *
 * Brian: *"in governing body, every governing body should include a stamp of
 * who is assigned. and in governing body, we should be able to sort by
 * assignment."*
 *
 * ⚠️ IT READS THE SAME ROWS THE CARD'S OTHER FOUR NUMBERS COME FROM, AND THAT
 * IS THE WHOLE DESIGN. The card already computes
 * `championshipsFor(e, data)` / `rollupFor(...)` for its championships,
 * crowned, no-date and defect figures. A second join would be a second answer
 * to one question, and a stamp that counted a different set than `tally.held`
 * would put a breakdown on the card that does not add up to the number beside
 * it — which is the reconciliation failure this lane exists to remove, not a
 * cosmetic mismatch. So the rollup takes the summary's own `rows` and never
 * re-matches the universe.
 *
 * ⚠️ DEFECTIVE-NAME ROWS ARE EXCLUDED, FOR THE SAME REASON. `summariseRows`
 * reports `held = rows.length - defective`, so counting assignment over all
 * rows would exceed `held` by the truncated records. The identity asserted by
 * verify-body-assignment.mjs is `records === tally.held`, and this is what
 * makes it true.
 *
 * ⚠️ TWO RESOLVERS, DELIBERATELY, IN THIS ORDER. `ownerOf` walks the RAW
 * HubSpot fields (`sr_assigned_rep`, `sr_lead_rep`, `sr_support_rep_1`) and is
 * what `bodyCoverage.js` uses; `ownerName` reads the NORMALIZED record. A row
 * here can be either shape depending on how the feed was loaded, so both are
 * tried and the raw field wins — it is the field the write touches.
 *
 * NO MONEY. Every figure in this file is a RECORD COUNT or a share of one.
 * These surfaces are ACTIONS.CC_VIEW, which every rep holds, and money is
 * manager-only.
 * ========================================================================= */
import { ownerOf } from './assignmentViews.js'
import { ownerName } from '../../lib/championships.js'
import { championshipsFor, rollupFor, isDefectiveName } from '../../lib/bodyChampionships.js'

const S = (v) => (typeof v === 'string' ? v.trim() : v == null ? '' : String(v).trim())

/**
 * The rep on one row, or '' when the seat is empty.
 *
 * ⚠️ '' IS A REAL ANSWER HERE AND IS NEVER COERCED TO A NAME. Inventing one is
 * how a body with unassigned records reads as fully covered — measured
 * 2026-10-13T21:09Z, 2,399 of 15,814 records name no rep.
 */
export function repOn(row) {
  const raw = row?.raw || row || {}
  return S(ownerOf(raw)) || S(ownerName(row)) || ''
}

/* ---- THE FIVE STATES ----------------------------------------------------- *
 *
 * Brian named four — unassigned, single, shared, partial. `empty` is the fifth
 * and it is not a widening of the ask: a card holding NO records at all is not
 * a card holding unassigned records, and rendering it as `unassigned` would
 * invent work that does not exist. `zeroKind()` already owns the "why is this
 * card at zero" sentence; this state exists so the stamp can defer to it
 * instead of printing a confident Unassigned over nothing.
 */
export const STATES = ['unassigned', 'partial', 'shared', 'single', 'empty']

/* The order "by assignment" sorts in, ascending: the gap first, the settled
 * last. `empty` sits at the end because there is nothing on it to action. */
export const STATE_RANK = { unassigned: 0, partial: 1, shared: 2, single: 3, empty: 4 }

/**
 * The assignment breakdown for one set of rows.
 *
 * Returns, always, for every state:
 *   records          total rows counted — equals the card's `tally.held`
 *   reps             [{ rep, count }] count DESC then name ASC
 *   unassigned       rows with an empty rep seat
 *   distinctReps     how many different people hold any of it
 *   dominant         { rep, count, share } | null — the largest holding, and
 *                    null when nobody holds any of it. NEVER an arbitrary pick:
 *                    a tie is broken by name so two renders cannot disagree.
 *   state            one of STATES
 */
export function assignmentOf(rows = []) {
  const counts = new Map()
  let records = 0
  let unassigned = 0

  for (const row of rows) {
    /* The truncated records are not titles and are not counted as held, so
     * they are not counted as assigned or unassigned either. */
    if (isDefectiveName(row)) continue
    records += 1
    const rep = repOn(row)
    if (!rep) { unassigned += 1; continue }
    counts.set(rep, (counts.get(rep) || 0) + 1)
  }

  const reps = [...counts.entries()]
    .map(([rep, count]) => ({ rep, count }))
    /* Count desc, then name asc — a STABLE order, so the dominant rep is a
     * fact about the rows rather than about Map insertion order. */
    .sort((a, b) => b.count - a.count || a.rep.localeCompare(b.rep))

  const dominant = reps.length
    ? { rep: reps[0].rep, count: reps[0].count, share: records ? reps[0].count / records : 0 }
    : null

  let state = 'empty'
  if (records > 0) {
    if (!reps.length) state = 'unassigned'
    else if (unassigned > 0) state = 'partial'
    else if (reps.length > 1) state = 'shared'
    else state = 'single'
  }

  return {
    records,
    reps,
    unassigned,
    distinctReps: reps.length,
    dominant,
    state,
    /* The property, not a claim. Asserted by the verify script and rendered on
     * the card when it is false, rather than silently papered over. */
    reconciled: unassigned + reps.reduce((n, r) => n + r.count, 0) === records,
  }
}

/**
 * One pass per tab: every entity's assignment, keyed by `entity.key`.
 *
 * ⚠️ ONE useMemo, NOT ONE PASS PER CARD PER RENDER. `championshipsFor` walks
 * the whole universe once per entity, so calling this inside the card loop
 * would be O(bodies x bodies x 15k) — the identical mistake the `peerBaseline`
 * comment on GoverningBodies.jsx was written to prevent.
 *
 * ⚠️ A ROLL-UP CARD'S SIBLINGS ARE THE FULL ENTITY LIST, NOT THE SEARCH
 * RESULTS. A body's assignment is a fact about the body, not about what
 * somebody typed in a search box — the same reasoning `peerBaseline` already
 * applies to the peer median.
 */
export function assignmentByKey(entities = [], universe = [], opts = {}) {
  const byKey = new Map()
  for (const e of entities) {
    const tally = e?.rollup
      ? rollupFor(e, entities, universe, opts)
      : championshipsFor(e, universe, opts)
    byKey.set(e.key, assignmentOf(tally.rows))
  }
  return byKey
}

/**
 * The tab's reconciliation: assigned + unassigned = every record counted on
 * the cards.
 *
 * ⚠️ `records` SUMS THE CARDS AND THEREFORE DOUBLE-COUNTS A RECORD TWO BODIES
 * BOTH CLAIM — exactly as the existing defect topline does, which states the
 * same caveat beside `audits.sum` and `audits.distinct`. `onTwoCards` is
 * carried so the page can say so rather than let a summed total read as the
 * size of the object.
 */
export function assignmentTotals(byKey, entities = []) {
  let records = 0
  let assigned = 0
  let unassigned = 0
  const reps = new Map()
  const byState = Object.fromEntries(STATES.map((s) => [s, 0]))

  for (const e of entities) {
    const a = byKey.get(e.key)
    if (!a) continue
    records += a.records
    unassigned += a.unassigned
    byState[a.state] += 1
    for (const r of a.reps) {
      assigned += r.count
      reps.set(r.rep, (reps.get(r.rep) || 0) + 1)
    }
  }

  return {
    records,
    assigned,
    unassigned,
    bodies: entities.length,
    distinctReps: reps.size,
    byState,
    /* The identity, computed rather than asserted. */
    reconciled: assigned + unassigned === records,
    shortfall: records - (assigned + unassigned),
  }
}

/* ---- THE SORT ------------------------------------------------------------ *
 *
 * 🔴 `makeComparator('owner')` IN lib/championships.js IS THE WRONG TOOL HERE
 * AND IS DELIBERATELY NOT REUSED. Its own comment reads "Blank owners are the
 * expected state right now — always sort last", and it sorts them last in BOTH
 * directions. On a record board that is defensible. On this page it would make
 * the unassigned bodies unreachable by sorting — the exact invisibility this
 * surface exists to remove. Brian: Unassigned must be a visible, SORTABLE
 * state.
 *
 * So here Unassigned is a VALUE. In `assignment` ascending it is the first
 * thing you see; descending, the fully-settled bodies come first and the gap is
 * at the other end. Both ends are reachable, which is the test.
 */
export const SORT_MODES = [
  {
    key: 'assignment',
    label: 'By assignment',
    why: 'Unassigned first, then part-assigned, then shared, then settled — and reversed the other way.',
  },
  {
    key: 'unassigned',
    label: 'By unassigned count',
    why: 'Most unassigned records first. This is the actionable one.',
  },
  { key: 'records', label: 'By records', why: 'Most championships first.' },
  { key: 'name', label: 'By name', why: 'Acronym A to Z.' },
]

export const SORT_BY_KEY = Object.fromEntries(SORT_MODES.map((m) => [m.key, m]))

const acr = (x) => S(x?.entity?.acronym) || S(x?.entity?.name)
/* The name an assignment group sorts under. Unassigned has no name, so it falls
 * to the acronym rather than being dropped out of the ordering. */
const repKey = (x) => S(x?.assignment?.dominant?.rep).toLowerCase()

/**
 * A comparator over `{ entity, assignment }` pairs.
 *
 * `dir` reverses the WHOLE comparison including the tie-breaks, so a reversed
 * list is the exact mirror of the forward one and cannot develop its own
 * order.
 */
export function compareBodies(mode = 'records', dir = 'asc') {
  const mul = dir === 'desc' ? -1 : 1
  return (a, b) => {
    let d = 0
    if (mode === 'assignment') {
      d = (STATE_RANK[a?.assignment?.state] ?? 9) - (STATE_RANK[b?.assignment?.state] ?? 9)
      if (!d) d = repKey(a).localeCompare(repKey(b))
    } else if (mode === 'unassigned') {
      d = (b?.assignment?.unassigned || 0) - (a?.assignment?.unassigned || 0)
      if (!d) d = (b?.assignment?.records || 0) - (a?.assignment?.records || 0)
    } else if (mode === 'records') {
      d = (b?.assignment?.records || 0) - (a?.assignment?.records || 0)
    } else {
      d = acr(a).localeCompare(acr(b))
    }
    if (!d) d = acr(a).localeCompare(acr(b))
    return d * mul
  }
}

/**
 * Order the cards, and REPORT WHAT WAS ACTUALLY DONE.
 *
 * ⚠️ THE CHIP READS THIS, NOT THE CLICK. `ordering.mode` is the mode the sort
 * really applied, which is the pattern already asserted on the worklist's
 * order bar (`data-on={result.ordering.mode === k}`). An assignment sort over a
 * feed that has not loaded would order every card by a zero and present the
 * result as if it meant something, so it falls back and says why instead.
 */
export function orderBodies(entities = [], byKey, { mode = 'records', dir = 'asc', loaded = true } = {}) {
  const pairs = entities.map((entity) => ({ entity, assignment: byKey?.get(entity.key) || assignmentOf([]) }))
  const wantsAssignment = mode === 'assignment' || mode === 'unassigned'
  const available = Boolean(loaded)
  const applied = wantsAssignment && !available ? 'name' : (SORT_BY_KEY[mode] ? mode : 'records')

  pairs.sort(compareBodies(applied, dir))

  return {
    pairs,
    entities: pairs.map((p) => p.entity),
    ordering: {
      mode: applied,
      requested: mode,
      dir,
      assignmentAvailable: available,
      whyNotAssignment: available
        ? ''
        : 'The championship feed has not finished loading, so no card knows yet how many of its '
          + 'records name a rep. Sorting by assignment now would order every card by the same zero '
          + 'and look like an answer.',
    },
  }
}
